import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/http';
import { tokenStore } from '@/lib/auth';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';

export const Route = createFileRoute('/grupos/$id')({ component: GroupDetailPage });

function GroupDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isLoggedIn = !!tokenStore.getAccess();

  const { data, isLoading } = useQuery({
    queryKey: ['group', id],
    queryFn: () => api.get(`groups/${id}`).json<any>(),
  });

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('auth/me').json<any>(),
    enabled: isLoggedIn,
  });

  const join = useMutation({
    mutationFn: () => api.post(`groups/${id}/join`).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['group', id] });
      navigate({ to: '/dashboard/sessoes' });
    },
  });

  const isFull = data && (data.participants?.length ?? 0) >= (data.maxParticipants ?? 0);
  const alreadyJoined =
    !!me?.clientProfile && data?.participants?.some((p: any) => p.clientId === me.clientProfile.id);

  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">
        {isLoading ? (
          <div className="bg-white rounded-3xl p-12 animate-pulse h-64" />
        ) : !data ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
            <p className="text-slate-500">Grupo não encontrado.</p>
          </div>
        ) : (
          <article className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-full px-4 py-1.5 text-teal-700 text-sm font-medium mb-4">
              Sessão em grupo · {data.modality === 'ONLINE' ? 'Online' : 'Presencial'}
            </div>
            <h1 className="text-3xl font-extrabold text-slate-800">{data.title}</h1>
            {data.description && (
              <p className="mt-3 text-slate-600 leading-relaxed whitespace-pre-line">{data.description}</p>
            )}

            <div className="mt-6 grid grid-cols-3 gap-4 text-sm">
              <Stat label="Quando" value={new Date(data.scheduledAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} />
              <Stat label="Duração" value={`${data.durationMin} min`} />
              <Stat label="Vagas" value={`${data.participants?.length ?? 0}/${data.maxParticipants}`} />
            </div>

            {data.therapist && (
              <div className="mt-6 pt-6 border-t border-slate-100 flex items-center gap-3">
                {data.therapist.user?.image ? (
                  <img src={data.therapist.user.image} alt="" className="size-12 rounded-full object-cover" />
                ) : (
                  <div className="size-12 rounded-full bg-gradient-to-br from-teal-300 to-teal-500" />
                )}
                <div>
                  <p className="text-xs text-slate-400">Conduzido por</p>
                  <p className="font-bold text-slate-700">{data.therapist.user?.name}</p>
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs text-slate-400">Por participante</p>
                <p className="text-2xl font-extrabold text-teal-600">R$ {Number(data.pricePerSeat).toFixed(2)}</p>
              </div>

              {alreadyJoined ? (
                <span className="bg-teal-50 text-teal-700 border border-teal-200 px-7 py-3 rounded-full font-medium">
                  ✓ Vaga reservada
                </span>
              ) : isFull ? (
                <span className="bg-slate-100 text-slate-500 px-7 py-3 rounded-full font-medium">
                  Sem vagas
                </span>
              ) : !isLoggedIn ? (
                <Link
                  to="/login"
                  search={{ next: `/grupos/${id}` } as any}
                  className="bg-coral-400 hover:bg-coral-500 text-white px-7 py-3 rounded-full font-medium shadow transition"
                >
                  Entrar para reservar
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => join.mutate()}
                  disabled={join.isPending}
                  className="bg-coral-400 hover:bg-coral-500 disabled:opacity-50 text-white px-7 py-3 rounded-full font-medium shadow transition"
                >
                  {join.isPending ? 'Reservando...' : 'Reservar vaga'}
                </button>
              )}
            </div>

            {join.isError && (
              <p className="mt-3 text-sm text-red-600 text-right">
                Não foi possível reservar. Tente novamente.
              </p>
            )}
          </article>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded-2xl p-3 text-center">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-sm font-bold text-slate-700 mt-0.5">{value}</p>
    </div>
  );
}
